import {Button, Link, Tooltip, Typography} from '@mui/material';
import type {GridColDef} from '@mui/x-data-grid-premium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {centered, emptyCell, subLine} from '~/components/data-grid/cells/gridCells';
import {Tag} from '~/components/tag/Tag';
import type {TagColor} from '~/components/tag/tag.styles';
import {MONO_FONT} from '~/style/fonts';
import type {ExploreRow} from '../domain/exploreTree';
import type {Risk} from '../types/categoryProfileTypes';

export const RISK_LABEL: Record<Risk, string> = {high: 'High risk', medium: 'Medium risk', low: 'Low risk'};
const RISK_COLOR: Record<Risk, TagColor> = {high: 'error', medium: 'warning', low: 'success'};

export function riskTag(risk: Risk, tooltip?: string) {
    const tag = <Tag size="small" color={RISK_COLOR[risk]} label={RISK_LABEL[risk]}/>;
    return tooltip ? <Tooltip title={tooltip}><span>{tag}</span></Tooltip> : tag;
}

export function yoyTag(yoyPct: number | null) {
    if (yoyPct === null) return emptyCell();
    const up = yoyPct >= 0;
    return centered(
        <Tooltip title="Spend change against the same months a year earlier">
            <Typography variant="body2" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5,
                color: up ? 'warning.main' : 'success.main'}}>
                {up ? <TrendingUpIcon fontSize="small"/> : <TrendingDownIcon fontSize="small"/>}
                {`${up ? '+' : ''}${yoyPct.toFixed(1)}%`}
            </Typography>
        </Tooltip>,
    );
}

type Options = {
    money: (v: number) => string;
    onViewDetails: (row: ExploreRow) => void;
    /** In tree mode the grid's grouping column shows the name; in the flat level view this column does. */
    withNameColumn: boolean;
};

/** Columns of the Explore Categories grid (spec 3.6). The category name is the tree's grouping column. */
export function getExploreColumns({money, onViewDetails, withNameColumn}: Options): GridColDef<ExploreRow>[] {
    const name: GridColDef<ExploreRow> = {
        field: 'name', headerName: 'Category', width: 280,
        renderCell: ({row}) => centered(<>
            <Link component="button" underline="hover" color="inherit" variant="body2" noWrap title={`Open ${row.name}`}
                  onClick={e => {
                      e.stopPropagation();
                      onViewDetails(row);
                  }}
                  sx={{fontWeight: 500, textAlign: 'left', alignSelf: 'flex-start', maxWidth: '100%'}}>
                {row.name}
            </Link>
            {categoryCellCaption(row)}
        </>),
    };
    return [
        ...(withNameColumn ? [name] : []),
        {
            field: 'code', headerName: 'ID', width: 90,
            renderCell: ({row}) => row.code
                ? centered(<Typography variant="body2" sx={{fontFamily: MONO_FONT}}>{row.code}</Typography>)
                : emptyCell(),
        },
        {
            field: 'spend', headerName: 'Total spend', type: 'number', width: 120,
            valueFormatter: (v: number) => money(v),
        },
        {
            field: 'spendSharePct', headerName: 'Spend %', type: 'number', width: 90,
            valueFormatter: (v: number) => `${v.toFixed(1)}%`,
        },
        {field: 'suppliers', headerName: 'Suppliers', type: 'number', width: 100},
        {field: 'transactions', headerName: 'Transactions', type: 'number', width: 120},
        {
            field: 'yoyPct', headerName: 'YoY', width: 100,
            renderCell: ({row}) => yoyTag(row.yoyPct),
        },
        {
            field: 'risk', headerName: 'Risk', width: 120, type: 'singleSelect',
            valueOptions: ['high', 'medium', 'low'],
            renderCell: ({row}) => centered(riskTag(row.risk,
                `Supplier concentration: ${money(row.spendPerSupplier)} per supplier`)),
        },
        {
            field: 'actions', headerName: '', width: 120, sortable: false, filterable: false, align: 'right',
            disableExport: true,
            renderCell: ({row}) => centered(
                <Button size="small" variant="text" onClick={e => {
                    e.stopPropagation();
                    onViewDetails(row);
                }}>
                    View details
                </Button>,
            ),
        },
    ];
}

export function categoryCellCaption(row: ExploreRow) {
    if (row.rank && row.parentPath) return subLine(row.parentPath);
    const n = row.children.length;
    return subLine(n ? `${n} sub-categor${n === 1 ? 'y' : 'ies'}` : 'No sub-categories');
}
